import { createUserManager } from 'redux-oidc';
import { apiConfig } from '../_config';

// Some External ID tenants emit tokens whose issuer uses the tenantId as BOTH the subdomain and path segment:
//   iss = https://{tenantId}.ciamlogin.com/{tenantId}/v2.0
// We previously assumed the pattern with tenantName + .onmicrosoft.com.
// Your error showed: Invalid issuer in token: https://<tenantId>.ciamlogin.com/<tenantId>/v2.0
// So we align our configured metadata issuer and endpoints to that pattern to satisfy validation.
// If later your tenant begins issuing with the tenant name domain, flip USE_TENANT_ID_DOMAIN to false.
const USE_TENANT_ID_DOMAIN = true;
const ciamDomainSub = USE_TENANT_ID_DOMAIN ? apiConfig.entraTenantId : apiConfig.entraTenantName;
const ciamPathSegment = USE_TENANT_ID_DOMAIN ? apiConfig.entraTenantId : `${apiConfig.entraTenantName}.onmicrosoft.com`;
// Base authority host for Entra External ID (CIAM) using selected pattern
const ENTRA_AUTHORITY_BASE = `https://${ciamDomainSub}.ciamlogin.com/${ciamPathSegment}`;

const entraUserManagerConfig = {
    client_id: apiConfig.entraClientId,
    redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/callback`,
    response_type: 'code',
    response_mode: 'query',
    // Explicit PKCE enablement (response_type 'code' already triggers PKCE in oidc-client; these are for clarity)
    usePkce: true,
    code_challenge_method: 'S256',
    // Do NOT append client id to scopes for Entra External ID
    scope: 'openid profile offline_access',
    authority: ENTRA_AUTHORITY_BASE,
    silent_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/silent-renew`,
    automaticSilentRenew: true,
    monitorSession: true,
    filterProtocolClaims: false,
    loadUserInfo: false, // keep false (no userinfo call needed)
    revokeAccessTokenOnSignout: true,
    post_logout_redirect_uri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/loggedout`,
    metadata: {
        // Issuer must match exactly the 'iss' in the token (no trailing slash)
        issuer: `https://${ciamDomainSub}.ciamlogin.com/${apiConfig.entraTenantId}/v2.0`,
        authorization_endpoint: `${ENTRA_AUTHORITY_BASE}/oauth2/v2.0/authorize`,
        token_endpoint: `${ENTRA_AUTHORITY_BASE}/oauth2/v2.0/token`,
        end_session_endpoint: `${ENTRA_AUTHORITY_BASE}/oauth2/v2.0/logout`,
        jwks_uri: `${ENTRA_AUTHORITY_BASE}/discovery/v2.0/keys`
    }
};

const entraUserManager = createUserManager(entraUserManagerConfig);

// Debug logging to help diagnose redirect/code processing
try {
    entraUserManager.events.addUserLoaded(user => {
        console.log('[EntraUserManager] user loaded', user && { expired: user.expired, profile: user.profile });
    });
    entraUserManager.events.addSilentRenewError(e => {
        console.error('[EntraUserManager] silent renew error', e);
    });
    entraUserManager.events.addUserUnloaded(() => {
        console.log('[EntraUserManager] user unloaded');
    });
    entraUserManager.events.addAccessTokenExpired(() => {
        console.log('[EntraUserManager] access token expired');
    });
    entraUserManager.events.addAccessTokenExpiring(() => {
        console.log('[EntraUserManager] access token expiring');
    });
} catch(e) {
    console.warn('Could not attach entraUserManager event handlers', e);
}

export default entraUserManager;
export { ENTRA_AUTHORITY_BASE };
